import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, emailNote, useFetch } from "@/lib/api";
import { fmtDate, gbp, JOB_STATUSES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Loading, ErrorBox, Section, StatusBadge, DemoTag, NativeSelect, Field, Detail } from "@/components/Common";
import { SimpleTable } from "@/components/SimpleTable";
import { ActivityList, NotesPanel } from "@/components/Activity";

export function Jobs() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState({ status: params.get("status") || "", q: "", date_from: "", date_to: "" });
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  const { data, loading, error } = useFetch(`/admin/jobs?${qs}`);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <PageHeader title="Jobs" />
      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Input placeholder="Search reference, customer, address" aria-label="Search jobs" value={f.q} onChange={set("q")} data-testid="jobs-search-input" />
        <NativeSelect aria-label="Status filter" value={f.status} onChange={set("status")} data-testid="jobs-status-filter"><option value="">All statuses</option>{JOB_STATUSES.map((s) => <option key={s}>{s}</option>)}</NativeSelect>
        <Input type="date" aria-label="Planned start from" value={f.date_from} onChange={set("date_from")} data-testid="jobs-date-from" />
        <Input type="date" aria-label="Planned start to" value={f.date_to} onChange={set("date_to")} data-testid="jobs-date-to" />
      </div>
      <ErrorBox error={error} />
      {loading && !data ? <Loading /> : (
        <SimpleTable testId="jobs-table" rows={data || []} onRow={(j) => nav(`/admin/jobs/${j.id}`)} rowTestId={(j) => `job-row-${j.reference}`}
          cols={[
            { label: "Reference", render: (j) => <span className="font-medium">{j.reference}<DemoTag show={j.is_demo} /></span> },
            { label: "Customer", render: (j) => j.customer_name },
            { label: "Property", render: (j) => j.property_address },
            { label: "Price", render: (j) => gbp(j.agreed_price) },
            { label: "Planned start", render: (j) => fmtDate(j.planned_start) },
            { label: "Assigned", render: (j) => j.assigned_staff_name || "—" },
            { label: "Status", render: (j) => <StatusBadge status={j.status} /> },
          ]} />
      )}
    </div>
  );
}

export function JobDetail() {
  const { id } = useParams();
  const { data: j, loading, error, reload } = useFetch(`/admin/jobs/${id}`);
  const { data: staff } = useFetch("/admin/staff");
  const [f, setF] = useState(null);
  if (loading && !j) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const form = f || { status: j.status, description: j.description, agreed_price: j.agreed_price || "", planned_start: j.planned_start || "", expected_completion: j.expected_completion || "", assigned_staff_id: j.assigned_staff_id || "" };
  const set = (k) => (e) => setF({ ...form, [k]: e.target.value });
  const save = async () => {
    try { const r = await api.patch(`/admin/jobs/${id}`, form); toast.success(`Job updated. ${emailNote(r.data.email_status)}`); setF(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5">
      <PageHeader title={<>Job {j.reference}<DemoTag show={j.is_demo} /></>} subtitle={<>Customer <Link className="underline" to={`/admin/customers/${j.customer_id}`}>{j.customer?.name}</Link> · Request <Link className="underline" to={`/admin/requests/${j.request_id}`}>{j.request_reference}</Link>{j.quotation_reference && <> · Quotation <Link className="underline" to={`/admin/quotations/${j.quotation_id}`}>{j.quotation_reference}</Link></>}</>}
        actions={<StatusBadge status={j.status} testId="admin-job-status" />} />
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Section title="Job details">
            <dl className="mb-4 grid gap-3 sm:grid-cols-3"><Detail label="Property">{j.property_address}</Detail><Detail label="Customer email">{j.customer?.email}</Detail><Detail label="Telephone">{j.customer?.phone}</Detail></dl>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Status" id="js"><NativeSelect id="js" value={form.status} onChange={set("status")} data-testid="job-status-select">{JOB_STATUSES.map((s) => <option key={s}>{s}</option>)}</NativeSelect></Field>
              <Field label="Agreed price (£)" id="jp"><Input id="jp" type="number" step="0.01" min="0" value={form.agreed_price} onChange={set("agreed_price")} data-testid="job-price-input" /></Field>
              <Field label="Planned start" id="jst"><Input id="jst" type="date" value={form.planned_start} onChange={set("planned_start")} data-testid="job-start-input" /></Field>
              <Field label="Expected completion" id="jc"><Input id="jc" type="date" value={form.expected_completion} onChange={set("expected_completion")} data-testid="job-completion-input" /></Field>
              <Field label="Assigned staff member" id="ja"><NativeSelect id="ja" value={form.assigned_staff_id} onChange={set("assigned_staff_id")} data-testid="job-staff-select"><option value="">Unassigned</option>{staff?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</NativeSelect></Field>
            </div>
            <div className="mt-4"><Field label="Project description" id="jd"><Textarea id="jd" rows={3} value={form.description} onChange={set("description")} data-testid="job-description-input" /></Field></div>
            <Button className="mt-4" onClick={save} data-testid="job-save-btn">Save changes</Button>
          </Section>
          <Section title="Activity history"><ActivityList items={j.activity} /></Section>
        </div>
        <NotesPanel notes={j.notes} postUrl={`/admin/jobs/${id}/notes`} onAdded={reload} />
      </div>
    </div>
  );
}
